/// <reference path="../../common/common.module.ts" />

/**
 * Web Component Bridge Service
 *
 * Provides host runtime context (auth, session, API base URL, security keys,
 * app settings) and a pub/sub lifecycle event system to any embedded web
 * component. This service is domain-agnostic.
 *
 * Registered on `meevo.components.common` so it is available to all plugins.
 *
 * --- Bridge contract ---
 * Web components receive this service as a property (default: `hostBridge`).
 * They use:
 *   bridge.initialize()                → initial context snapshot
 *   bridge.getCurrentContext()          → on-demand context refresh
 *   bridge.subscribe(listener)         → lifecycle events (context-changed, session-state-changed)
 *   bridge.refreshLockoutLastEventTime → keep lockout timer alive
 */
angular.module('meevo.components.common').factory('webComponentBridge', [
    '$injector',
    '$rootScope',
    'meevo.AppSettings',
    'meevo.components.common.IdentityService',
    function (
        $injector: angular.auto.IInjectorService,
        $rootScope: angular.IRootScopeService,
        appSettings: Meevo.Components.Common.IAppSettings,
        identityService: Meevo.Components.Common.IIdentityService,
    ) {
        const LOG_PREFIX = '[WebComponentBridge][HostInterop]';
        const listeners: Array<(event: any) => void> = [];
        let lastIdentitySignature = '';

        function log(message: string, details?: any): void {
            if (details === undefined) {
                console.log(LOG_PREFIX + ' ' + message);
                return;
            }
            console.log(LOG_PREFIX + ' ' + message, details);
        }

        function warn(message: string, details?: any): void {
            if (details === undefined) {
                console.warn(LOG_PREFIX + ' ' + message);
                return;
            }
            console.warn(LOG_PREFIX + ' ' + message, details);
        }

        function contextSnapshot(context: any): any {
            return {
                hasContext: !!context,
                apiBaseUrl: context ? context.apiBaseUrl : null,
                hasSession: !!(context && context.session),
                session: context && context.session
                    ? {
                        tenantId: context.session.tenantId || null,
                        locationId: context.session.locationId || null,
                        locationName: context.session.locationName || null,
                        userName: context.session.userName || context.session.userAuthName || null,
                        hasBearerToken: !!context.session.bearerToken,
                    }
                    : null,
                securityItemCount: context && context.securityItems ? context.securityItems.length : 0,
            };
        }

        function getIdentitySignature(identity: Meevo.Components.Common.IIdentity | null | undefined): string {
            if (!identity) {
                return '';
            }
            return [
                identity.userId,
                identity.locationId,
                identity.effectiveCallCenterId,
                identity.asImpersonateLocationId,
            ].join('|');
        }

        async function resolveSecurityItems(): Promise<any[]> {
            try {
                const resolved = await Promise.resolve(appSettings.SECURITY_ACCESS_KEYS);
                return Array.isArray(resolved) ? resolved : [];
            } catch (_error) {
                warn('failed to resolve security items; returning empty array');
                return [];
            }
        }

        function getInteropService(): any | null {
            if (!$injector.has('meevo.plugins.interop.InteropService')) {
                return null;
            }
            try {
                return $injector.get('meevo.plugins.interop.InteropService');
            } catch (_error) {
                warn('InteropService resolve threw; returning null');
                return null;
            }
        }

        async function buildContext(): Promise<any> {
            const interopService = getInteropService();

            if (interopService && typeof interopService.getHostRuntimeContext === 'function') {
                const interopContext = await Promise.resolve(interopService.getHostRuntimeContext());
                log('buildContext from InteropService.getHostRuntimeContext', contextSnapshot(interopContext));
                return interopContext;
            }

            // Fallback: assemble context directly from shell services
            const identity = identityService.current();
            const session = identity
                ? {
                    ...identity,
                    bearerToken: (window as any).bearerToken !== undefined && (window as any).bearerToken !== null
                        ? (window as any).bearerToken
                        : null,
                }
                : null;
            const securityItems = await resolveSecurityItems();

            const context = {
                apiBaseUrl: `/${appSettings.API_ROOT}`,
                session,
                appSettings: {
                    API_ROOT: appSettings.API_ROOT,
                    APP_COUNTRYID: appSettings.APP_COUNTRYID,
                    APP_LANGUAGEID: appSettings.APP_LANGUAGEID,
                    APP_TIME_FORMAT: appSettings.APP_TIME_FORMAT,
                    APP_ISOCOUNTRYCODE: appSettings.APP_ISOCOUNTRYCODE,
                    APP_FIELDLABELS: appSettings.APP_FIELDLABELS,
                    APP_BUSINFOSETTINGS: appSettings.APP_BUSINFOSETTINGS,
                    SECURITY_ACCESS_KEYS: securityItems,
                },
                securityItems,
            };

            log('buildContext from fallback identity/appSettings', contextSnapshot(context));
            return context;
        }

        async function emit(event: any): Promise<void> {
            const snapshot = listeners.slice();
            for (const listener of snapshot) {
                try {
                    listener(event);
                } catch (error) {
                    console.warn('[WebComponentBridge] Listener failed.', error);
                }
            }
        }

        async function emitContextChanged(reason: string): Promise<void> {
            await emit({
                type: 'context-changed',
                reason,
                context: await buildContext(),
            });
        }

        // --- Lifecycle event listeners ---
        $rootScope.$on(Meevo.Components.Common.Messages.AUTHENTICATION_SUCCESS, async () => {
            const identity = identityService.current();
            const nextSignature = getIdentitySignature(identity);
            const reason = lastIdentitySignature && lastIdentitySignature !== nextSignature
                ? 'account-switch'
                : 'authentication-success';
            lastIdentitySignature = nextSignature;
            await emitContextChanged(reason);
        });

        $rootScope.$on(Meevo.Components.Common.Messages.REFRESH_SECURITY_KEYS, async () => {
            await emitContextChanged('session-refresh');
        });

        $rootScope.$on(Meevo.Components.Common.Messages.FORCE_SESSION_RELOAD, async () => {
            await emitContextChanged('force-session-reload');
        });

        $rootScope.$on(
            Meevo.Components.Common.Messages.SESSION_TIMEOUT_HANDLER,
            async (_event: angular.IAngularEvent, sessionState: Meevo.Model.SessionState) => {
                await emit({
                    type: 'session-state-changed',
                    reason: sessionState === Meevo.Model.SessionState.Lockout ? 'lockout' : 'logout',
                    context: await buildContext(),
                });
            }
        );

        return {
            initialize: async function () {
                const context = await buildContext();
                lastIdentitySignature = getIdentitySignature(context.session);
                return context;
            },
            getCurrentContext: function () {
                return buildContext();
            },
            refreshLockoutLastEventTime: function () {
                const interopService = getInteropService();
                if (interopService && typeof interopService.refreshLockoutLastEventTime === 'function') {
                    interopService.refreshLockoutLastEventTime();
                    return;
                }
                $rootScope.$broadcast(Meevo.Components.Common.Messages.REFRESH_LOCKOUT_LAST_EVENT_TIME);
            },
            subscribe: function (listener: (event: any) => void) {
                listeners.push(listener);
                return function () {
                    const index = listeners.indexOf(listener);
                    if (index >= 0) {
                        listeners.splice(index, 1);
                    }
                };
            },
            /**
             * Diagnostic — returns the current listener count. Used by the
             * mount directive to detect listener leaks across mount/unmount
             * cycles. Not part of the component-facing contract.
             */
            getListenerCount: function (): number {
                return listeners.length;
            },
        };
    }
]);
